import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LANG_STORAGE_KEY } from '../../i18n';
import {
  Menu as MenuIcon,
  Dashboard,
  Chat,
  People,
  Settings,
  Assessment,
  Label,
  Group,
  QuestionAnswer,
  Schedule,
  Notifications,
  ExitToApp,
  Person,
  Campaign,
  Hub,
  ManageAccounts,
  ContactPage,
  Inbox,
  Extension,
  Autorenew,
  LibraryBooks,
  Security,  // Added: 2024-11-12 - User Access Management Module Phase 4.5
  ThumbsUpDown,  // Added: 2025-11-18 - CSAT Analytics
  WhatsApp, // Added: 2026-01-19 - WhatsApp Templates
  Email as EmailIcon, // Added: 09/02/2026 - Email Templates
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { useKeyboardShortcuts } from '../../contexts/KeyboardShortcutsContext';
import CommandBar from '../CommandBar/CommandBar';
import ShortcutsHelpModal from '../KeyboardShortcuts/ShortcutsHelpModal';
import NotificationsDropdown from '../Notifications/NotificationsDropdown';
import { conversationsAPI } from '../../services/api';
import { usePermission, useAnyPermission } from '../../hooks/usePermission'; // 14/11/2025 - User Access Management Module Phase 5.2.2

const drawerWidth = 260;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [conversationCount, setConversationCount] = useState<number>(0);
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(localStorage.getItem(LANG_STORAGE_KEY) || i18n.language || 'fr');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const { isCommandBarOpen, isHelpModalOpen, closeCommandBar, closeHelpModal } = useKeyboardShortcuts();

  const handleLangChange = (newLang: string) => {
    setLang(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem(LANG_STORAGE_KEY, newLang);
  };

  // Fetch total conversation count for sidebar badge (all statuses).
  // Matches "All" tab count on Conversations page so left-panel total and All (76) stay in sync.
  useEffect(() => {
    const fetchConversationCount = async () => {
      try {
        const response = await conversationsAPI.getConversations({
          per_page: 1
        });
        setConversationCount(response.total ?? response.conversations?.length ?? 0);
      } catch (error) {
        console.error('Failed to fetch conversation count:', error);
      }
    };

    fetchConversationCount();

    // Poll every 30 seconds to keep count updated
    const interval = setInterval(fetchConversationCount, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleProfileMenuClose();
  };

  // 14/11/2025 - User Access Management Module Phase 5.2.2
  // Define all menu items with required permissions (textKey → i18n nav.*)
  const allMenuItems = [
    { textKey: 'nav.dashboard', icon: <Dashboard />, path: '/dashboard' },
    { textKey: 'nav.conversations', icon: <Chat />, path: '/dashboard/conversations', badge: conversationCount, requiredPermissions: ['conversations:view_all', 'conversations:view_team', 'conversations:view_assigned'] },
    { textKey: 'nav.linelist', icon: <People />, path: '/dashboard/contacts', requiredPermissions: ['contacts:view'] },
    { textKey: 'nav.inboxes', icon: <Inbox />, path: '/dashboard/inboxes', requiredPermissions: ['inboxes:view'] },
    { textKey: 'nav.integrations', icon: <Extension />, path: '/dashboard/integrations', requiredPermissions: ['integrations:view'] },
    { textKey: 'nav.reports', icon: <Assessment />, path: '/dashboard/reports', requiredPermissions: ['reports:view'] },
    { textKey: 'nav.automation', icon: <Autorenew />, path: '/dashboard/automation', requiredPermissions: ['automation:view'] },
    { textKey: 'nav.labels', icon: <Label />, path: '/dashboard/labels', requiredPermissions: ['labels:view'] },
    { textKey: 'nav.teams', icon: <Group />, path: '/dashboard/teams', requiredPermissions: ['teams:view'] },
    { textKey: 'nav.agents', icon: <Person />, path: '/dashboard/agents', requiredPermissions: ['agents:view'] },
    { textKey: 'nav.rolesPermissions', icon: <Security />, path: '/dashboard/roles', requiredPermissions: ['roles:view'] },
    { textKey: 'nav.cannedResponses', icon: <QuestionAnswer />, path: '/dashboard/canned-responses', requiredPermissions: ['canned_responses:view'] },
    { textKey: 'nav.campaigns', icon: <Campaign />, path: '/dashboard/campaigns', requiredPermissions: ['campaigns:view'] },
    { textKey: 'nav.whatsappTemplates', icon: <WhatsApp />, path: '/dashboard/whatsapp-templates', requiredPermissions: ['campaigns:view'] },
    { textKey: 'nav.emailTemplates', icon: <EmailIcon />, path: '/dashboard/email-templates', requiredPermissions: ['campaigns:view'] },
    { textKey: 'nav.preChatForms', icon: <ContactPage />, path: '/dashboard/pre-chat-forms', requiredPermissions: ['pre_chat_forms:view'] },
    { textKey: 'nav.agentCapacity', icon: <ManageAccounts />, path: '/dashboard/agent-capacity' },
    { textKey: 'nav.slackIntegration', icon: <Hub />, path: '/dashboard/slack-integration', requiredPermissions: ['slack_integration:view'] },
    { textKey: 'nav.businessHours', icon: <Schedule />, path: '/dashboard/business-hours', requiredPermissions: ['business_hours:view'] },
    { textKey: 'nav.knowledgeBase', icon: <LibraryBooks />, path: '/dashboard/knowledge-base', requiredPermissions: ['knowledge_base:view'] },
    { textKey: 'nav.csatAnalytics', icon: <ThumbsUpDown />, path: '/dashboard/csat-analytics' },
    { textKey: 'nav.settings', icon: <Settings />, path: '/dashboard/settings', requiredPermissions: ['settings:view'] },
  ];

  // 14/11/2025 - User Access Management Module Phase 5.2.2
  // Filter menu items based on user permissions
  const menuItems = useMemo(() => {
    const role = (user?.role || '').toUpperCase();

    // Super admins see everything
    if (role === 'SUPER_ADMIN') {
      return allMenuItems;
    }

    // Admin: Dashboard + Linelist + Agents (to create Admin/Agent accounts)
    if (role === 'ADMIN') {
      return allMenuItems.filter(
        (item) =>
          item.path === '/dashboard' ||
          item.path === '/dashboard/contacts' ||
          item.path === '/dashboard/agents'
      );
    }

    // Agent: Dashboard + Linelist only
    if (role === 'AGENT') {
      return allMenuItems.filter(
        (item) => item.path === '/dashboard' || item.path === '/dashboard/contacts'
      );
    }

    // Filter based on permissions
    return allMenuItems.filter(item => {
      // Items without required permissions are always shown (like Dashboard, Agent Capacity)
      if (!item.requiredPermissions) {
        return true;
      }

      // Check if user has ANY of the required permissions (OR logic)
      return item.requiredPermissions.some(perm =>
        user?.permissions?.includes(perm)
      );
    });
  }, [user?.permissions, user?.role, conversationCount, i18n.language]);

  const pageTitleKey = useMemo(() => {
    const p = location.pathname;
    if (p === '/dashboard' || p === '/dashboard/') return 'nav.dashboard';
    if (p.includes('conversations')) return 'nav.conversations';
    if (p.includes('contacts')) return 'nav.linelist';
    if (p.includes('inboxes')) return 'nav.inboxes';
    if (p.includes('integrations')) return 'nav.integrations';
    if (p.includes('reports')) return 'nav.reports';
    if (p.includes('automation')) return 'nav.automation';
    if (p.includes('labels')) return 'nav.labels';
    if (p.includes('teams')) return 'nav.teams';
    if (p.includes('agents')) return 'nav.agents';
    if (p.includes('roles')) return 'nav.rolesPermissions';
    if (p.includes('canned-responses')) return 'nav.cannedResponses';
    if (p.includes('campaigns')) return 'nav.campaigns';
    if (p.includes('whatsapp-templates')) return 'nav.whatsappTemplates';
    if (p.includes('email-templates')) return 'nav.emailTemplates';
    if (p.includes('pre-chat-forms')) return 'nav.preChatForms';
    if (p.includes('agent-capacity')) return 'nav.agentCapacity';
    if (p.includes('slack-integration')) return 'nav.slackIntegration';
    if (p.includes('business-hours')) return 'nav.businessHours';
    if (p.includes('knowledge-base')) return 'nav.knowledgeBase';
    if (p.includes('csat-analytics')) return 'nav.csatAnalytics';
    if (p.includes('settings')) return 'nav.settings';
    return 'nav.dashboard';
  }, [location.pathname]);

  // Admin: Dashboard + Linelist + Agents. Agent: Dashboard + Linelist only.
  useEffect(() => {
    const role = (user?.role || '').toUpperCase();
    if (role !== 'ADMIN' && role !== 'AGENT') return;
    const path = location.pathname;
    const allowedCommon =
      path === '/dashboard' ||
      path === '/dashboard/' ||
      path.startsWith('/dashboard/contacts') ||
      path.startsWith('/dashboard/profile') ||
      path.startsWith('/dashboard/my-profile');
    const allowed =
      role === 'ADMIN'
        ? allowedCommon || path.startsWith('/dashboard/agents')
        : allowedCommon;
    if (!allowed) {
      navigate('/dashboard', { replace: true });
    }
  }, [user?.role, location.pathname, navigate]);

  const isSelected = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
          <img
            src="/logo/ebola_logo.png"
            alt="Ebola"
            style={{
              height: '52px',
              width: '180px',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.textKey} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={isSelected(item.path)}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                  color: '#1f93ff',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ 
                color: isSelected(item.path) ? '#1f93ff' : 'inherit',
                minWidth: 40 
              }}>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText 
                primary={t(item.textKey)} 
                primaryTypographyProps={{ fontSize: '0.9rem' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'black',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {t(pageTitleKey)}
          </Typography>
          
          <ToggleButtonGroup
            size="small"
            value={lang.startsWith('fr') ? 'fr' : 'en'}
            exclusive
            onChange={(_e, newLang) => newLang && handleLangChange(newLang)}
            aria-label={t('lang.switchLanguage')}
            sx={{ mr: 1.5 }}
          >
            <ToggleButton value="fr" sx={{ px: 1.25, py: 0.25, textTransform: 'none', fontSize: '0.75rem' }}>
              FR
            </ToggleButton>
            <ToggleButton value="en" sx={{ px: 1.25, py: 0.25, textTransform: 'none', fontSize: '0.75rem' }}>
              EN
            </ToggleButton>
          </ToggleButtonGroup>

          <NotificationsDropdown />
          
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ p: 0 }}
          >
            <Avatar
              src={user?.avatar_url || undefined}
              sx={{ bgcolor: '#1f93ff' }}
            >
              {user?.first_name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => navigate('/dashboard/settings')}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              {t('nav.profile')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              {t('nav.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid #e0e0e0',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>
      
      {/* Command Bar */}
      <CommandBar open={isCommandBarOpen} onClose={closeCommandBar} />
      
      {/* Keyboard Shortcuts Help Modal */}
      <ShortcutsHelpModal open={isHelpModalOpen} onClose={closeHelpModal} />
    </Box>
  );
};

export default DashboardLayout;